import * as Sentry from '@sentry/browser';
import posthog from 'posthog-js';

import { SdkError } from 'wg-api-sdk';

import type { CRM } from '../types';

import useActionsAnva from './useActionsAnva';
import useActionsAssu from './useActionsAssu';
import useActionsBrio from './useActionsBrio';
import useActionsDias from './useActionsDias';
import useActionsFasterForward from './useActionsFasterForward';

export default function useExportPartyToCrms() {
  const actionsAnva = useActionsAnva();
  const actionsDias = useActionsDias();
  const actionsAssu = useActionsAssu();
  const actionsBrio = useActionsBrio();
  const actionsFasterForward = useActionsFasterForward();

  const exportPartyByCrm: Record<
    CRM,
    (variables: { partyId: string }) => Promise<unknown>
  > = {
    anva: actionsAnva.exportParty,
    dias: actionsDias.exportParty,
    assu: actionsAssu.exportParty,
    brio: actionsBrio.exportParty,
    fasterforward: actionsFasterForward.exportParty,
  };

  const exportParty = async (partyId: string, crms: Array<CRM>) => {
    const results = await Promise.allSettled(
      crms.map((crm) => exportPartyByCrm[crm]({ partyId })),
    );

    const failedCrms: Array<CRM> = [];
    const errorMessageByCrm: Partial<Record<CRM, string>> = {};

    results.forEach((result, index) => {
      const crm = crms[index];

      if (result.status === 'fulfilled' || !crm) return;

      failedCrms.push(crm);

      if (result.reason instanceof SdkError) {
        errorMessageByCrm[crm] = result.reason.message;
      }

      posthog.capture('Party export to CRM failed', {
        crmBackend: crm,
        partyId,
        error: String(result.reason),
      });
      Sentry.captureException(result.reason, { tags: { crm } });
    });

    return { failedCrms, errorMessageByCrm };
  };

  return { exportParty };
}
